//NOTE: this file contains all relavant utility functions/hooks for the expeditions page
//if any of the functions are later to be shared by any components that does not belong
//to the expedition feature, then the function should be refactor (if necessary) and moved
//to the general util.js file in the client root

import React from 'react'
import PropTypes from 'prop-types'
import _ from 'lodash'
import { runQuery } from '../../fetchUtils'
import { isPlural } from '../../util'
import moment from 'moment'

//ENUMS
export const MOVE = 'move'
export const SWAP = 'swap'
export const UNGROUPED = 'ungrouped'
export const GROUPED = 'grouped'
export const DATE = 'date'

//functions
export const handleDeleteModeConversion = assignment => {
	const isSelfAssignment = assignment.isSelfAssignment
	return {
		expedition: {
			id: assignment.expeditionId,
			version: assignment.expeditionVersion,
			expedition_name: assignment.assignments[0].quest.name,
			expedition_description: assignment.description
		},
		assignedStudentGroups: {
			expeditionId: assignment.expeditionId,
			expeditionVersionNumber: assignment.expeditionVersion,
			isSelfAssignment: isSelfAssignment,
			sectionId: assignment.classId,
			sectionName: isSelfAssignment ? 'Myself' : assignment.sectionName,
			assignedDate: assignment.toBeAssignedAt,
			expeditionInstanceIds: assignment.assignments.map(
				assignmentInstance => assignmentInstance.id
			),
			...(assignment.isSelfAssignment
				? {
						userUuid: assignment.userUuid
				  }
				: {
						groups: assignment.assignments.map(studentGroup => {
							return studentGroup.students.map(student => ({
								[student.id]: student.full_name
							}))
						})
				  })
		}
	}
}

//context
export const ExpeditionContext = React.createContext()

const ExpeditionContextProvider = ({ children, assignedStudentGroups, isDelete }) => {
	const [isLoading, setIsLoading] = React.useState(false)
	const [isSubmitted, setIsSubmitted] = React.useState(false)
	const [selectedDate, setSelectedDate] = React.useState(
		isDelete ? moment(assignedStudentGroups.assignedDate).utc().format('MM/DD/YYYY') : ''
	)
	const [assignableSections, setAssignableSections] = React.useState([])
	const [studentGroups, setStudentGroups] = React.useState(
		assignedStudentGroups || {
			groups: [],
			ungroupedStudents: []
		}
	)
	const [assignmentMessage, setAssignmentMessage] = React.useState({})

	const expeditionContextValue = React.useMemo(() => ({
		isLoading,
		setIsLoading,
		isSubmitted,
		setIsSubmitted,
		selectedDate,
		setSelectedDate,
		assignableSections,
		setAssignableSections,
		studentGroups,
		setStudentGroups,
		assignmentMessage,
		setAssignmentMessage,
		isDelete
	}))

	React.useEffect(() => {
		const handleFetchAssignableClass = () => {
			const fetchAssignableClassQuery = `
				query LoadAssignableClass{
					sections{
						id
						name
						courseId
						course{
							name
						}
						students{
							id
							userUuid
							profile{
								fullName
							}
						}
					}
				}
			`

			const fetchAssignableClassCallBack = {
				onComplete: json => {
					const error = json.errors
					if (!_.isEmpty(error)) {
						console.error(error)
					} else {
						const sectionsByCourse = _.groupBy(
							json.data.sections,
							section => section.courseId
						)
						setAssignableSections(sectionsByCourse)
					}
				},
				onFailure: error => {
					console.error(error)
				}
			}
			const vars = {}

			runQuery(fetchAssignableClassQuery, vars, fetchAssignableClassCallBack)
		}

		if (!isDelete && _.isEmpty(assignableSections)) {
			handleFetchAssignableClass()
		}

		document.body.style.overflow = 'hidden'
		return () => (document.body.style.overflow = 'scroll')
	}, [])

	React.useEffect(() => {
		setAssignmentMessage({})
		setIsSubmitted(false)
	}, [studentGroups, selectedDate])

	return (
		<ExpeditionContext.Provider value={expeditionContextValue}>
			{children}
		</ExpeditionContext.Provider>
	)
}

ExpeditionContextProvider.propTypes = {
	children: PropTypes.element.isRequired,
	isDelete: PropTypes.bool.isRequired,
	assignedStudentGroups: PropTypes.oneOfType([PropTypes.object, PropTypes.bool])
}

export default ExpeditionContextProvider

//hooks
export const useExpeditionUtils = () => {
	const {
		setIsLoading,
		studentGroups,
		setStudentGroups,
		setAssignmentMessage,
		selectedDate,
		setIsSubmitted
	} = React.useContext(ExpeditionContext)
	// const currentUser = useSelector(state => state.currentUser)

	//Student group modifications
	const handleCreateStudentGroups = students => {
		let maxBucketSize = 5
		const bucketAmount = Math.ceil(students.length / maxBucketSize)
		let groups = new Array(bucketAmount)
		for (let i = 0; i < bucketAmount; i++) {
			groups[i] = []
		}

		let bucketInd = 0
		_.forEach(students, (student, ind) => {
			groups[bucketInd].push({ [student.userUuid]: student.profile.fullName })
			bucketInd !== bucketAmount - 1 ? bucketInd++ : (bucketInd = 0)
		})

		return groups
	}

	const handleDeleteGroup = groupKey => {
		setStudentGroups(prev => {
			const removedGroup = prev.groups.splice(groupKey, 1)
			return {
				...prev,
				ungroupedStudents: _.flatten([
					!_.isEmpty(prev.ungroupedStudents) ? prev.ungroupedStudents : [],
					...removedGroup
				])
			}
		})
	}

	const handleRemoveStudent = student => {
		setStudentGroups(prev => {
			const removedStudent = prev.groups[student.groupIndex][student.studentIndex]
			prev.groups[student.groupIndex].splice(student.studentIndex, 1)
			prev.ungroupedStudents.push(removedStudent)
			return { ...prev }
		})
	}

	const handleMoveStudentGroup = (student, toGroupIndex) => {
		if (!_.isNumber(student.groupIndex) && studentGroups.groups[toGroupIndex].length < 5) {
			setStudentGroups(prev => {
				const movedStudent = prev[student.groupIndex][student.studentIndex]
				prev[student.groupIndex].splice(student.studentIndex, 1)
				prev.groups[toGroupIndex].push(movedStudent)
				return { ...prev }
			})
		} else if (
			student.groupIndex !== toGroupIndex &&
			studentGroups.groups[toGroupIndex].length < 5
		) {
			setStudentGroups(prev => {
				const movedStudent = prev.groups[student.groupIndex][student.studentIndex]
				prev.groups[student.groupIndex].splice(student.studentIndex, 1)
				prev.groups[toGroupIndex].push(movedStudent)
				return { ...prev }
			})
		}
	}

	const handleSwapStudentGroup = (fromStudent, toStudent) => {
		setStudentGroups(prev => {
			const swapStudent = prev.groups[fromStudent.groupIndex][fromStudent.studentIndex]
			const swapTarget = prev.groups[toStudent.groupIndex][toStudent.studentIndex]

			prev.groups[fromStudent.groupIndex].splice(fromStudent.studentIndex, 1, swapTarget)

			prev.groups[toStudent.groupIndex].splice(toStudent.studentIndex, 1, swapStudent)

			return { ...prev }
		})
	}

	//handle assignemnt creations
	const handleAssignExpedition = data => {
		setIsLoading(true)
		const assignExpeditionQuery = `
			mutation CreateExpeditionsAssignment($expeditionId: ID!, $expeditionVersionNumber: Int!, $assignedDate: String!, $isSelfAssignment: Boolean! ${
				data.isSelfAssignment
					? ''
					: ', $groups: [ExpeditionGroupsInputObject!]!, $sectionId: ID!, $sectionName: String!'
			}){
			createExpeditionsAssignment(expeditionId: $expeditionId, expeditionVersionNumber: $expeditionVersionNumber, assignedDate: $assignedDate, isSelfAssignment: $isSelfAssignment ${
				data.isSelfAssignment
					? ''
					: ', groups: $groups, sectionId: $sectionId, sectionName:$sectionName'
			}){
				wasSuccessful,
				errorMessage
			}
		}
		`
		const assignExpeditionCallBack = {
			onComplete: json => {
				setIsLoading(false)
				const error = !json.data?.createExpeditionsAssignment?.wasSuccessful || json?.error
				const errorMessage = json.data?.createExpeditionsAssignment?.errorMessage

				if (!error) {
					setIsSubmitted(true)
					setAssignmentMessage({
						success: true,
						message: data.isSelfAssignment
							? '1 assignment created for you'
							: `1 class assignment created for ${
									_.flatten(data.groups).length
							  } students as ${data.groups.length} group${isPlural(
									data.groups.length
							  )}.`
					})
				} else {
					setAssignmentMessage({
						success: false,
						message: !_.isEmpty(errorMessage)
							? errorMessage
							: 'Oops, something went wrong. Please make sure that all required fields are filled in.'
					})
				}
			},
			onFailure: error => {
				setIsLoading(false)
				console.error(error)
			}
		}
		const vars = {
			...data,
			...(!data.isSelfAssignment && {
				groups: _.map(data.groups, studentGroup => {
					let studentIds = []
					let studentNames = []
					_.forEach(studentGroup, student => {
						studentIds.push(Object.keys(student))
						studentNames.push(student[Object.keys(student)])
					})

					return {
						studentIds: _.flatten(studentIds),
						studentNames: studentNames
					}
				})
			}),
			assignedDate: moment(selectedDate).utc(true).toISOString()
		}

		if (handleSubmissionValidation(selectedDate)) {
			runQuery(assignExpeditionQuery, vars, assignExpeditionCallBack)
		} else {
			setIsLoading(false)
		}
	}

	const handleSubmissionValidation = submissionDate => {
		if (!studentGroups.isSelfAssignment && !handleValidateGroupSum()) {
			setAssignmentMessage({
				success: false,
				message: `Assignment creation failed because there are too many requests at the same time. This submission include ${
					studentGroups.groups.length
				} groups and ${
					_.flatten(studentGroups.groups).length
				} students. The sum of number of groups and number of students can not be greater than 100. Please try only assigning part of the class at a time.`
			})
		} else if (!studentGroups.isSelfAssignment && !_.isEmpty(handleValidateStudentNum())) {
			setAssignmentMessage({
				success: false,
				message:
					'Assignment creation failed because the following groups do not have the required number (3-5) of members.',
				detail: handleValidateStudentNum()
			})
		} else if (!handleValidateDate(submissionDate)) {
			setAssignmentMessage({
				success: false,
				type: DATE,
				message: 'Invalid date. Please select a date.'
			})
		} else {
			setAssignmentMessage({ success: true, message: '' })
			return true
		}
		return false
	}

	const handleValidateGroupSum = () => {
		//NOTE: due to constraint from dynamoDB, validating that the sum of #of groups + # of total students is less than 100
		return studentGroups.groups.length + _.flatten(studentGroups.groups).length < 100
	}
	const handleValidateStudentNum = () => {
		//NOTE: validate that each group must contain from 3-5 students
		/*
			invalidGroup: {
				0: #of students
			}
		*/
		let invalidGroup = {}
		_.forEach(studentGroups.groups, (group, ind) => {
			if (group.length > 5 || group.length < 3) {
				invalidGroup[ind] = group.length
			}
		})

		return invalidGroup
	}

	const handleValidateDate = date => {
		return !_.isEmpty(date) && moment(date).isValid()
	}

	//handle assignment deletion
	const handleDeleteAssignment = assignment => {
		console.log('deleting assignment...')
		const instanceIds = assignment.expeditionInstanceIds
		const sectionId = assignment.sectionId
		setIsLoading(true)
		const deleteExpeditionQuery = `
			mutation DeleteExpeditionsAssignment($expeditionInstanceIds: [ID!]!, $isSelfAssignment: Boolean! ${
				!assignment.isSelfAssignment ? ',$sectionId: ID' : ''
			}){
				deleteExpeditionsAssignment(expeditionInstanceIds: $expeditionInstanceIds, isSelfAssignment: $isSelfAssignment ${
					!assignment.isSelfAssignment ? ',sectionId: $sectionId' : ''
				}){
					wasSuccessful,
					message
				}
			}
		`

		const vars = {
			expeditionInstanceIds: instanceIds,
			isSelfAssignment: assignment.isSelfAssignment,
			...(!assignment.isSelfAssignment && { sectionId: sectionId })
		}

		const deleteExpeditionCallBack = {
			onComplete: json => {
				const error = !json.data.deleteExpeditionsAssignment?.wasSuccessful || json?.errors
				const message =
					json.data.deleteExpeditionsAssignment?.message || json.errors[0].message

				if (!error) {
					setIsSubmitted(true)
					setAssignmentMessage({
						success: true,
						message: message
					})
				} else {
					setAssignmentMessage({
						success: false,
						message: message
					})
				}
				setIsLoading(false)
			},
			onFailure: error => {
				console.error(error)
				setIsLoading(false)
				setAssignmentMessage({
					success: false,
					message: 'Oops! Something went wrong...'
				})
			}
		}

		runQuery(deleteExpeditionQuery, vars, deleteExpeditionCallBack)
	}

	return {
		handleCreateStudentGroups,
		handleDeleteGroup,
		handleRemoveStudent,
		handleMoveStudentGroup,
		handleSwapStudentGroup,
		handleAssignExpedition,
		handleSubmissionValidation,
		handleDeleteAssignment
	}
}
